import React, { useState, useEffect, useRef } from 'react';
import { DBCharacter } from '../../services/CharacterService';
import { PreGameLayout } from './components/PreGameLayout';
import { AuthScreen } from './screens/AuthScreen';
import { GreeterScreen } from './screens/GreeterScreen';
import { ServerSelectScreen } from './screens/ServerSelectScreen';
import { CharacterSelectScreen } from './screens/CharacterSelectScreen';
import { resolveUrlClassCharacter, willAutoPickFromUrl } from './resolveUrlClassCharacter';
import { freshCharacterEntryUrl, navigateFreshCharacter } from './freshCharacterEntry';
import { resumeCharacterEntryUrl, navigateResumeCharacter } from './resumeCharacterEntry';
import { guestCharacterEntryUrl } from './guestCharacterEntry';
import { isMainGameDomain } from '../../../config/environment/environment';
import { storage } from '../../services/StorageService';
import { gameLogger } from '../../../utils/GameLogger';
import { CharacterCustomizer } from '../Menus/CharacterCustomizer';
import { ApiClient } from '../../../services/ApiClient';
import './PreGame.css';
import { bootHref, bootSearchParams } from '../../../config/boot/bootUrl';

// CharacterSelectScreen (roster + delete + create tile) is the only
// character-select surface. QuickClassPickerScreen was retired 2026-08-06: its
// nine class cards duplicated CharacterCustomizer, which its own footer linked
// to. The `?class=X` auto-pick that lived underneath those cards moved to
// resolveUrlClassCharacter and is still honored below, so every probe URL is
// unchanged — `?quickPicker` is now simply ignored.

// Character bridge — when a one-click flow (the editor Test button, a mode card)
// wants to KEEP the already-picked character instead of re-showing the picker,
// it passes `?keepCharacter=1`. An explicit `?pickCharacter=1` always wins (force
// the picker). Normal/fresh visits carry neither, so the default funnel is
// untouched. The last-selected character is persisted on select (below).
const shouldKeepCharacter = (): boolean => {
  try {
    const sp = bootSearchParams();
    if (sp.get('pickCharacter') === '1') return false; // explicit force-pick wins
    return sp.get('keepCharacter') === '1';
  } catch { return false; }
};
const readLastCharacter = (): DBCharacter | null => storage.getObject<DBCharacter>('helix_last_character');

// `?createCharacter=1` (injected by /intro via coerceModeUrl) routes a fresh
// player straight to the full character creator instead of the quick picker —
// "make a hero and start the story" as one card.
const wantsCreateCharacter = (): boolean => {
  try {
    return bootSearchParams().get('createCharacter') === '1';
  } catch { return false; }
};

interface PreGameFlowProps {
  onStartGame: (token: string, character: DBCharacter) => void;
  onLogout?: () => void;
  initialToken?: string | null;
}

type FlowState = 'auth' | 'greeter' | 'server_select' | 'character_select' | 'character_create';
interface CreationRequest { token: string; generation: number; pending: boolean }

/**
 * PreGameFlow — The AAA MMO pre-gameplay funnel orchestrator.
 * Manages the state machine transitioning players from Login to GameScene.
 */
export const PreGameFlow: React.FC<PreGameFlowProps> = ({ onStartGame, onLogout, initialToken }) => {
  const [currentStep, setCurrentStep] = useState<FlowState>(initialToken ? 'character_select' : 'auth');
  const [sessionToken, setSessionToken] = useState<string | null>(initialToken || null);
  const [username, setUsername] = useState<string>('');
  const [autoSelect, setAutoSelect] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string>('');
  const [guestError, setGuestError] = useState<string>('');
  const [guestLoading] = useState<boolean>(false);
  const [isGuestMode, setIsGuestMode] = useState<boolean>(false);
  const [creating, setCreating] = useState(false);
  const [rosterRevision, setRosterRevision] = useState(0);
  // The creator offers "Back to characters" only when there are heroes to go back to (stream note 48).
  const [creatorHasRoster, setCreatorHasRoster] = useState(false);
  const mountedRef = useRef(false);
  const creatorGeneration = useRef(0);
  const creationRequest = useRef<CreationRequest | null>(null);
  const creationExpiry = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flowOwner = useRef({ token: sessionToken, step: currentStep });
  flowOwner.current = { token: sessionToken, step: currentStep };
  const retireCreation = () => {
    creatorGeneration.current += 1;
    creationRequest.current = null;
    if (creationExpiry.current) clearTimeout(creationExpiry.current);
    creationExpiry.current = null;
    if (mountedRef.current) setCreating(false);
  };
  const ownsCreation = (request: CreationRequest) => mountedRef.current
    && (flowOwner.current.token === request.token || (isGuestMode && request.token === 'guest'))
    && flowOwner.current.step === 'character_create'
    && creatorGeneration.current === request.generation && creationRequest.current === request;
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; retireCreation(); };
  }, []);
  const resumeNavigationPendingRef = useRef(false);
  // Hold the roster back while a `?class=X` auto-pick is in flight. Decided
  // synchronously at mount so the roster never paints a frame before the
  // resolved character replaces it — the retired picker auto-picked before
  // painting, and losing that showed up immediately in VQA as a one-frame
  // "Select Character" flash on every probe URL.
  const [urlClassPending, setUrlClassPending] = useState<boolean>(() => willAutoPickFromUrl());

  // Local session bypass removed to enforce real backend authentication
  useEffect(() => {
    retireCreation();
    if (initialToken) {
      setSessionToken(initialToken);
      setCurrentStep('character_select');
    } else {
      setSessionToken(null);
      setCurrentStep('auth');
    }
  }, [initialToken]);

  // -- Transition Handlers --

  const handleAuthComplete = (session: { token: string; username: string; autoSelect?: boolean }) => {
    retireCreation();
    storage.set('helix_jwt', session.token);
    setSessionToken(session.token);
    setUsername(session.username);
    setAutoSelect(!!session.autoSelect);
    
    if (session.autoSelect) {
      setCurrentStep('character_select');
    } else {
      setCurrentStep('greeter');
    }
  };

  // Guest play flow: opens CharacterCustomizer so the player can design their
  // character before entering the 2-hour session (or proceed immediately with
  // the pre-rolled character).
  const handleGuestPlay = () => {
    retireCreation();
    setGuestError('');
    setIsGuestMode(true);
    setCreatorHasRoster(false);
    setCurrentStep('character_create');
  };

  const handleGreeterComplete = () => {
    setCurrentStep('server_select');
  };

  const handleServerSelectComplete = () => {
    setCurrentStep('character_select');
  };

  const handleCharacterSelectComplete = (char: DBCharacter) => {
    if (!sessionToken || resumeNavigationPendingRef.current) return;
    const resumeUrl = resumeCharacterEntryUrl(char, new URL(bootHref()), isMainGameDomain());
    if (resumeUrl) {
      setCreateError('');
      // The full page boot must recover this exact API snapshot, including its
      // saved map/position/progress. Matching only the id could accept an older
      // snapshot of this same hero when a storage write silently fails.
      try { storage.setObject('helix_last_character', char); } catch { /* verified below */ }
      if (JSON.stringify(readLastCharacter()) !== JSON.stringify(char)) {
        setCreateError('Your browser could not keep this hero selection. Enable site storage and try again.');
        return;
      }
      resumeNavigationPendingRef.current = true;
      try { navigateResumeCharacter(resumeUrl); } catch {
        resumeNavigationPendingRef.current = false;
        setCreateError('Your hero could not be opened. Try selecting the hero again.');
      }
      return;
    }
    // Other entry owners retain their existing map/transfer behavior.
    try { storage.setObject('helix_last_character', char); } catch { /* storage full / disabled */ }
    onStartGame(sessionToken, char);
  };

  // Open the full character creator (CharacterCustomizer) from the picker.
  // Without a roster context (`?createCharacter=1`) there is no known way back.
  const handleOpenCreator = (context?: { rosterEmpty: boolean }) => {
    retireCreation();
    setIsGuestMode(false);
    setCreateError('');
    setCreatorHasRoster(context ? !context.rosterEmpty : false);
    setCurrentStep('character_create');
  };
  const returnToRoster = () => {
    retireCreation();
    setAutoSelect(false);
    setCreateError('');
    if (isGuestMode) {
      setIsGuestMode(false);
      setCurrentStep('auth');
      return;
    }
    setCurrentStep('character_select');
  };

  // Persist a fully-customized character (name + class + appearance) via the
  // real API, then enter the game with it. This is the proven-persistence path:
  // the server stores name/class/appearance on the Character doc, so the hero
  // (and its look) survives a reload. Mirrors the URL auto-pick's create call but
  // with the rich appearance from the customizer instead of MIN_APPEARANCE.
  const handleCreateCharacter = async (name: string, charClass: string, appearance: any) => {
    if ((!sessionToken && !isGuestMode) || currentStep !== 'character_create' || creationRequest.current?.pending) return;
    if (creationExpiry.current) clearTimeout(creationExpiry.current);
    creationExpiry.current = null;
    const request: CreationRequest = { token: sessionToken || 'guest', generation: creatorGeneration.current, pending: true };
    creationRequest.current = request;
    setCreating(true);
    setCreateError('');
    try {
      if (isGuestMode) {
        gameLogger.info('auth', `👤 Requesting guest token with custom ${charClass} "${name}"`);
        const resp = await fetch('/api/auth/guest/issue', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, characterClass: charClass, appearance }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.message || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (!data.success || !data.token || !data.character) {
          throw new Error('Malformed guest response');
        }
        gameLogger.info('auth', `👤 Guest token issued: ${data.userId}`);
        storage.set('helix_jwt', data.token);
        const guest = data.character as DBCharacter;
        const entryUrl = guestCharacterEntryUrl(guest, new URL(bootHref()));
        if (entryUrl) {
          try { storage.setObject('helix_last_character', guest); } catch { /* verified below */ }
          if (readLastCharacter()?._id !== guest._id) {
            throw new Error('Your browser could not keep the guest hero. Enable site storage and try again.');
          }
          navigateFreshCharacter(entryUrl);
          return;
        }
        setSessionToken(data.token);
        setUsername(data.userId);
        onStartGame(data.token, guest);
        return;
      }

      gameLogger.info('character', `✨ Creating custom ${charClass} "${name}"`);
      const char = await ApiClient.createCharacter(request.token, {
        name, class: charClass, appearance, pin: '000',
      });
      if (!ownsCreation(request)) {
        // Cancellation cannot undo a successful save. Refresh only this owner's roster;
        // never enter the saved hero or disturb a newly opened creator/account.
        if (mountedRef.current && flowOwner.current.token === request.token
          && flowOwner.current.step === 'character_select') setRosterRevision(value => value + 1);
        return;
      }
      const entryUrl = freshCharacterEntryUrl(char.levelId, new URL(bootHref()));
      if (entryUrl) {
        storage.setObject('helix_last_character', char);
        if (readLastCharacter()?._id !== char._id) {
          setCreateError('Your hero was created, but this browser could not save the selection. Enable site storage to continue.');
          return;
        }
        navigateFreshCharacter(entryUrl);
        return;
      }
      handleCharacterSelectComplete(char as DBCharacter);
    } catch (e: any) {
      if (!ownsCreation(request)) return;
      gameLogger.error('character', 'Custom character create failed:', e);
      if (e?.status === 409 || /already exists/i.test(e?.message ?? '')) {
        setCreateError(e?.message || 'A hero with that name already exists.');
      } else if (/\b(?:401|403)\b/.test(e?.message ?? '')) {
        setCreateError('Session expired. Redirecting to login...');
        creationExpiry.current = setTimeout(() => {
          if (ownsCreation(request)) handleSessionInvalid();
        }, 1500);
      } else {
        setCreateError(`Failed to create character: ${e?.message || 'unknown error'}`);
      }
    } finally {
      if (ownsCreation(request)) {
        request.pending = false;
        setCreating(false);
      }
    }
  };

  // URL entry resolution — the three ways a URL can skip the roster, in strict
  // precedence order, first match wins. Fires once per mount (entryResolvedRef)
  // so cancelling the creator falls back to the roster rather than re-routing,
  // and so a resolved character is never re-picked.
  //
  //   1. `?keepCharacter=1` + a stored hero — full-page portal / editor-Test
  //      transfers re-enter with the character they left with.
  //   2. `?createCharacter=1` — the /intro "new adventure" card routes straight
  //      to the creator.
  //   3. `?class=X` — the headless probe / ClassSwitcherTopBar path.
  //
  // ClassSwitcherTopBar deletes `keepCharacter` before reloading precisely so a
  // class switch beats rule 1; that is load-bearing, not incidental.
  const entryResolvedRef = useRef(false);
  useEffect(() => {
    if (currentStep !== 'character_select' || !sessionToken || entryResolvedRef.current) return;

    // 1 — returning player wins.
    if (shouldKeepCharacter()) {
      const last = readLastCharacter();
      if (last) {
        entryResolvedRef.current = true;
        setUrlClassPending(false);
        gameLogger.info('auth', '🔗 keepCharacter — auto-selecting last character, skipping roster');
        handleCharacterSelectComplete(last);
        return;
      }
    }

    // 2 — explicit "make a new hero".
    if (wantsCreateCharacter()) {
      entryResolvedRef.current = true;
      setUrlClassPending(false);
      setCreateError('');
      gameLogger.info('character', '🌅 createCharacter — routing to the full creator');
      handleOpenCreator();
      return;
    }

    // 3 — headless `?class=X`. Resolves to null (and falls through to the
    // roster) when absent or suppressed.
    entryResolvedRef.current = true;
    void (async () => {
      try {
        const char = await resolveUrlClassCharacter(sessionToken);
        if (char) {
          handleCharacterSelectComplete(char);
          return; // leave the hold in place — we're entering the world
        }
        setUrlClassPending(false);
      } catch (e: any) {
        setUrlClassPending(false);
        gameLogger.error('character', 'URL class auto-pick failed:', e);
        // Unlike the retired picker — which stranded the player on a dead screen
        // with a red banner — a failure here surfaces the error and falls
        // through to the roster, which is always a route forward.
        if (e?.message?.includes('403')) {
          setCreateError('Session expired. Redirecting to login...');
          setTimeout(handleSessionInvalid, 1500);
        } else {
          setCreateError(`Failed to enter as that class: ${e?.message || 'unknown error'}`);
        }
      }
    })();
  }, [currentStep, sessionToken]);

  const handleSessionInvalid = () => {
    retireCreation();
    setIsGuestMode(false);
    gameLogger.warn('auth', '🚫 Session invalid or expired, clearing token and returning to login');
    storage.remove('helix_jwt');
    setSessionToken(null);
    setCurrentStep('auth');
    if (onLogout) onLogout();
  };

  const handleLogout = () => {
    retireCreation();
    setIsGuestMode(false);
    gameLogger.info('auth', '👋 User logging out from character selection');
    storage.remove('helix_jwt');
    setSessionToken(null);
    setCurrentStep('auth');
    if (onLogout) onLogout();
  };

  // -- Render State Machine --

  const renderCurrentScreen = () => {
    switch (currentStep) {
    case 'auth':
      return <AuthScreen onNext={handleAuthComplete} onGuestPlay={handleGuestPlay} guestError={guestError} guestLoading={guestLoading} />;
    case 'greeter':
      return <GreeterScreen username={username} onNext={handleGreeterComplete} />;
    case 'server_select':
      return <ServerSelectScreen onNext={handleServerSelectComplete} />;
    case 'character_select':
      // A `?class=X` URL is entering the world, not choosing — don't paint the
      // roster underneath an in-flight auto-pick.
      if (urlClassPending) {
        return (
          <div className="pregame-panel">
            <div className="pregame-title">Entering the world…</div>
          </div>
        );
      }
      return (
        <CharacterSelectScreen
          key={rosterRevision}
          sessionToken={sessionToken!}
          autoSelect={autoSelect}
          onSelectCharacter={handleCharacterSelectComplete}
          onCreateNew={handleOpenCreator}
          onSessionInvalid={handleSessionInvalid}
          onLogout={handleLogout}
          error={createError}
        />
      );
    default:
      return null;
    }
  };

  // The full character creator renders standalone (its own full-screen layout),
  // not inside the PreGameLayout funnel chrome.
  if (currentStep === 'character_create') {
    return (
      <CharacterCustomizer
        onCreate={handleCreateCharacter}
        submitting={creating}
        onCancel={returnToRoster}
        onBackToCharacters={creatorHasRoster ? returnToRoster : undefined}
        error={createError}
      />
    );
  }

  return (
    <PreGameLayout>
      {renderCurrentScreen()}
    </PreGameLayout>
  );
};
